//#region IMPORT
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAppNavigate } from '../../Hooks/back/useAppNavigate';
import { useSelector, useDispatch } from 'react-redux';
import Home, { ParseGuidFromQrValue } from './Home';
import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerAndroidScanningLibrary,
  CapacitorBarcodeScannerCameraDirection,
  CapacitorBarcodeScannerScanOrientation,
  CapacitorBarcodeScannerTypeHint,
} from '@capacitor/barcode-scanner';
import { showSnackbar } from '../../Reducer/SnackbarReducer';
import useFcmHandler from '../../Hooks/useFcmHandler';
import { BottomSheetActions } from '../../Reducer/BottomSheetReducer';
import { createInitial } from '../../util';
import { Home_Schema } from './Home_Schema';
import { useSave1, useSave3, useSave4, useSearch3 } from './Home_Query';
import { WorkingCheckActions } from '../../Reducer/WorkingCheckReducer';
import { useSave } from './../../Hooks/useSave';
import useFileUpload from './../../Hooks/useFileUpload';
import { TbmActions } from '../../Reducer/TbmReducer';
import { ParseTbmQrValue } from '../ToolBoxMeeting/GUEST/TbmJoinUrl';
import {
  TakePendingTbmJoinToken,
  TBM_JOIN_PENDING_EVENT,
} from '../ToolBoxMeeting/GUEST/TbmDeepLink';
//#endregion

function HomeFunction() {
  //#region 변수
  const { userInfo, menuInfo } = useSelector((state) => state.BaseInfo);
  const { workCd, workStopCd } = useSelector((state) => state.workingCheck);
  const { navigate } = useAppNavigate();
  const dispatch = useDispatch();

  const [workEndInputs, setWorkEndInputs] = useState(() =>
    createInitial(Home_Schema.fields.workEndForm)
  );

  const sendFcm = useFcmHandler();
  const workEndUpload = useFileUpload({
    oldFilePathField: 'image',
    newFileNameField: 'saveName',
    getTargetPath: (_, custCd) => `WorkEnd/${custCd}/WOE_BASIC`,
  });
  //#endregion

  //#region 조회/저장 로직
  const { runAsync: getManagersAsync } = useSearch3();
  const { runAsync: save1Async } = useSave1();
  const { runAsync: save3Async } = useSave3();
  const { runAsync: save4Async } = useSave4();

  // TBM 참석자 저장
  const { run: Save1 } = useSave({
    menuId: 'Home',
    requestSave: (rows) => save1Async(rows),
    onSuccess: (rows) => {
      if (rows.saveDetailData[0].type === 'warning') {
        dispatch(
          showSnackbar({
            message: rows.saveDetailData[0].message,
            type: 'warning',
          })
        );
        // 성공/실패 여부와 상관없이 QR 스캐너를 닫기 위해 뒤로가기 수행
        if (window.location.hash === '#qr') window.history.back();
      } else {
        const data = rows.saveDetailData[0].data[0];

        if (!data) {
          dispatch(
            showSnackbar({
              message: '유효한 QR코드가 아닙니다. 다시 스캔해 주세요.',
              type: 'warning',
            })
          );
        } else {
          dispatch(TbmActions.setParticipantData(data));
          navigate('/M_TBM');
        }
      }
    },
  });

  // 일반 카메라 QR(앱 링크)로 앱이 열린 경우: 보관된 입장 토큰으로 바로 참석 처리
  const save1Ref = useRef(Save1);
  save1Ref.current = Save1;
  useEffect(() => {
    const joinPending = () => {
      const token = TakePendingTbmJoinToken();
      if (token)
        save1Ref.current([{ sessionCd: token, hostYn: 'N', stayTime: 0 }]);
    };
    joinPending();
    window.addEventListener(TBM_JOIN_PENDING_EVENT, joinPending);
    return () =>
      window.removeEventListener(TBM_JOIN_PENDING_EVENT, joinPending);
  }, []);

  const { run: Save3 } = useSave({
    menuId: 'Home',
    schema: Home_Schema.fields.save3,
    saveData: () => ({ workStopCd: workStopCd }),
    requestSave: (rows) => save3Async(rows),
  });

  // 작업 종료 저장
  const { run: Save4 } = useSave({
    menuId: 'Home',
    schema: Home_Schema.fields.workEndForm,
    fileSave: async () => {
      const endImagePath = await workEndUpload.SaveLogicFile();
      return { beforeImage: endImagePath };
    },
    saveData: () => ({ ...workEndInputs, workCd }),
    requestSave: (rows) => save4Async(rows),
    onSuccess: async () => {
      const managers = await getManagersAsync({ userInfo });
      const workEndProgram = menuInfo?.find(
        (menu) => menu.menuId === 'M_WorkEnd2'
      )?.proId;
      const notificationRoute =
        workEndProgram === 'M_WOE_DAI' ? '/M_WorkInfo' : '';

      for (const manager of managers) {
        await sendFcm({
          targetUserId: manager.id,
          requestUserId: userInfo.userid,
          body: `${userInfo.userNm}님이 작업을 종료했습니다.`,
          status: '작업종료',
          codeGroup: 'AL_007',
          title: '작업종료',
          route: notificationRoute,
          responseData: workCd,
        });
      }
      workEndUpload.clearFiles();
      setWorkEndInputs(createInitial(Home_Schema.fields.workEndForm));
      dispatch(WorkingCheckActions.clearWorkEnd());

      // 바텀시트 해시가 있으면 제거 후 닫기
      if (window.location.hash.includes('#sheet-')) window.history.back();
      dispatch(BottomSheetActions.CloseBottomSheet());
    },
  });

  const ScanBarcode = async () => {
    try {
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.ALL,
        scanInstructions: 'QR코드를 스캔해 주세요.',
        scanButton: true,
        scanText: '스캔',
        cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
        scanOrientation: CapacitorBarcodeScannerScanOrientation.ADAPTIVE,
        android: {
          scanningLibrary: CapacitorBarcodeScannerAndroidScanningLibrary.ZXING,
        },
      });
      if (result.ScanResult) {
        // QR: 입장 URL(일반 카메라 겸용)이면 토큰, 예전 QR이면 세션번호 — 서버가 둘 다 처리
        const sessionValue = ParseTbmQrValue(result.ScanResult);
        if (!sessionValue) {
          dispatch(
            showSnackbar({
              message: '유효한 TBM QR코드가 아닙니다. 다시 스캔해 주세요.',
              type: 'warning',
            })
          );
          return;
        }
        Save1([{ sessionCd: sessionValue, hostYn: 'N', stayTime: 0 }]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  /** 안전보건교육 참석(M_TRA) — 네이티브 바코드 스캔 후 세션 GUID 검증 및 서명 화면 이동 */
  const ScanBarcodeTra = useCallback(async () => {
    try {
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.ALL,
        scanInstructions: '교육 참석 QR코드를 스캔해 주세요.',
        scanButton: true,
        scanText: '스캔',
        cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
        scanOrientation: CapacitorBarcodeScannerScanOrientation.ADAPTIVE,
        android: {
          scanningLibrary: CapacitorBarcodeScannerAndroidScanningLibrary.ZXING,
        },
      });
      if (!result.ScanResult) return;
      const sessionId = ParseGuidFromQrValue(result.ScanResult);
      if (!sessionId) {
        dispatch(
          showSnackbar({
            message:
              '유효한 교육 참석 QR(Session GUID)가 아닙니다. 다시 스캔해 주세요.',
            type: 'warning',
          })
        );
        return;
      }
      navigate(`/M_TRA/sign/${encodeURIComponent(sessionId)}`);
    } catch (e) {
      console.error(e);
    }
  }, [navigate, dispatch]);
  //#endregion

  return (
    <div className="wrap" style={{ height: 'calc(100% - 56px)' }}>
      <Home
        workEndInputs={workEndInputs}
        setWorkEndInputs={setWorkEndInputs}
        Save1={Save1}
        SaveLogic3={Save3}
        SaveLogic4={Save4}
        ScanBarcode={ScanBarcode}
        ScanBarcodeTra={ScanBarcodeTra}
        workEndUpload={workEndUpload}
      />
    </div>
  );
}

export default HomeFunction;
